import PulseApi from "@/services/pulseApi";
import {
  LOAD_NOTIFICATIONS,
  LOAD_NOTIFICATION,
  MARK_NOTIFICATION_READ,
  BULK_UPDATE_NOTIFICATIONS,
  LOAD_DROPDOWN_NOTIFICATIONS,
  LOAD_LAST_SEEN,
  UPDATE_LAST_SEEN
} from "@/store/_actiontypes";
import {
  SET_NOTIFICATIONS,
  SET_NOTIFICATION,
  UPDATE_NOTIFICATION,
  SET_SUPPRESS_BADGE,
  SET_DROPDOWN_NOTIFICATIONS,
  UPDATE_DROPDOWN_NOTIFICATION,
  SET_LAST_SEEN
} from "@/store/_mutationtypes";
import vuetify from "../../plugins/vuetify"

const STORAGE_KEY = "notification:suppressBadge";

let fallbackSuppressBadge = false;

function readSuppressBadgeFromStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return fallbackSuppressBadge;
  }
}

function _isRead(notification) {
  if (!notification) return false;
  if (typeof notification.is_read === "boolean") return notification.is_read;
  return !!notification.read_at;
}

function _computeUnreadCount(state) {
  const seen = new Set();
  const combined = [];

  (state.notifications || []).forEach(n => {
    if (n && n.id != null) {
      seen.add(String(n.id));
      combined.push(n);
    }
  });

  (state.dropdownNotifications || []).forEach(n => {
    if (!n || n.id == null) return;
    if (!seen.has(String(n.id))) {
      combined.push(n);
      seen.add(String(n.id));
    }
  });

  return combined.filter(n => !_isRead(n)).length;
}

const state = {
  notifications: [],
  dropdownNotifications: [],
  unreadCount: 0,
  lastSeenAt: null,
  suppressBadge: readSuppressBadgeFromStorage()
};

const mutations = {
  [SET_LAST_SEEN](state, payload = {}) {
    const lastSeen = payload.last_seen_at || payload.lastSeenAt || null;
    state.lastSeenAt = lastSeen;
    if (typeof payload.unread_count === "number") {
      state.unreadCount = payload.unread_count;
    } else if (typeof payload.unreadCount === "number") {
      state.unreadCount = payload.unreadCount;
    } else {
      state.unreadCount = _computeUnreadCount(state);
    }
  },

  [SET_NOTIFICATIONS](state, notifications = []) {
    state.notifications = notifications || [];
    if (!state.lastSeenAt) {
      state.unreadCount = _computeUnreadCount(state);
    }
  },

  [SET_NOTIFICATION](state, notification) {
    if (!notification || !notification.id) return;
    const idx = state.notifications.findIndex(item => String(item.id) === String(notification.id));
    if (idx === -1) {
      state.notifications.unshift(notification);
    } else {
      state.notifications.splice(idx, 1, notification);
    }
    if (!state.lastSeenAt) {
      state.unreadCount = _computeUnreadCount(state);
    }
  },

  [UPDATE_NOTIFICATION](state, partial) {
    if (!partial || !partial.id) return;
    const idx = state.notifications.findIndex(item => String(item.id) === String(partial.id));
    if (idx === -1) {
      state.notifications.unshift(partial);
    } else {
      const merged = Object.assign({}, state.notifications[idx], partial);
      state.notifications.splice(idx, 1, merged);
    }
    if (!state.lastSeenAt) {
      state.unreadCount = _computeUnreadCount(state);
    }
  },

  [SET_DROPDOWN_NOTIFICATIONS](state, notifications = []) {
    state.dropdownNotifications = notifications || [];
    if (!state.lastSeenAt) {
      state.unreadCount = _computeUnreadCount(state);
    }
  },

  [UPDATE_DROPDOWN_NOTIFICATION](state, partial) {
    if (!partial || !partial.id) return;
    const idx = state.dropdownNotifications.findIndex(item => String(item.id) === String(partial.id));
    if (idx === -1) {
      state.dropdownNotifications.unshift(partial);
      if (state.dropdownNotifications.length > 10) state.dropdownNotifications.length = 10;
    } else {
      const merged = Object.assign({}, state.dropdownNotifications[idx], partial);
      state.dropdownNotifications.splice(idx, 1, merged);
    }

    const pageIdx = state.notifications.findIndex(item => String(item.id) === String(partial.id));
    if (pageIdx !== -1) {
      const mergedPage = Object.assign({}, state.notifications[pageIdx], partial);
      state.notifications.splice(pageIdx, 1, mergedPage);
    }
    if (!state.lastSeenAt) {
      state.unreadCount = _computeUnreadCount(state);
    }
  },

  [SET_SUPPRESS_BADGE](state, val) {
    state.suppressBadge = !!val;
    try {
      localStorage.setItem(STORAGE_KEY, state.suppressBadge ? "1" : "0");
    } catch {
      fallbackSuppressBadge = state.suppressBadge;
    }
  }
};

const actions = {
  async [LOAD_NOTIFICATIONS]({ commit }, params = {}) {
    try {
      const res = await PulseApi.get("notify", { params: params });
      const data = res.data || {};
      const items = data.items || [];
      commit(SET_NOTIFICATIONS, items);
      return data;
    } catch (e) {
        const errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          vuetify.framework.lang.t('$vuetify.notifications.failed_to_load_notifications');

        commit(SET_NOTIFICATIONS, []);

        throw new Error(errorMessage);
      }
  },

  async [LOAD_NOTIFICATION]({ commit, state }, { notificationId } = {}) {
    if (!notificationId) return null;
    try {
      const res = await PulseApi.get(`notify/${notificationId}`);
      const item = res.data || null;
      if (item) {
        commit(UPDATE_DROPDOWN_NOTIFICATION, item);
        const existsInPage = (state.notifications || []).some(notification => String(notification.id) === String(item.id));
        if (existsInPage) commit(SET_NOTIFICATION, item);
      }
      return item;
    } catch {
      return null;
    }
  },

  async [LOAD_DROPDOWN_NOTIFICATIONS]({ commit }, params = { page: 1, per_page: 10 }) {
    try {
      const res = await PulseApi.get("notify", { params });
      const data = res.data || {};
      const items = data.items || [];
      commit(SET_DROPDOWN_NOTIFICATIONS, items);
      return data;
    } catch (e) {
        const errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          vuetify.framework.lang.t('$vuetify.notifications.failed_to_load_notifications');

        commit(SET_DROPDOWN_NOTIFICATIONS, []);

        throw new Error(errorMessage);
      }
  },

  async [LOAD_LAST_SEEN]({ commit }) {
    try {
      const res = await PulseApi.get("status/seen", {
        params: {
          service: 'console'
        }
      });
      const data = res.data || {};
      commit(SET_LAST_SEEN, data);
      return data;
    } catch {
      return null;
    }
  },

  async [UPDATE_LAST_SEEN]({ commit }, { last_seen_at } = {}) {
    if (!last_seen_at) {
      last_seen_at = new Date().toISOString();
    }
    try {
      const res = await PulseApi.put("status/seen", { last_seen_at });
      const data = res.data || {};
      commit(SET_LAST_SEEN, data);
      return data;
    } catch {
      return null;
    }
  },

  async [MARK_NOTIFICATION_READ]({ commit, state, dispatch }, { notificationId, isRead = true } = {}) {
    if (!notificationId) return null;
    const payload = {
      is_read: !!isRead,
      read_at: isRead ? new Date().toISOString() : null
    };
    try {
      const res = await PulseApi.put(`notify/${notificationId}`, payload);
      const updated = res.data || null;
      if (updated) {
        commit(UPDATE_DROPDOWN_NOTIFICATION, updated);
        const existsInPage = (state.notifications || []).some(notification => String(notification.id) === String(updated.id));
        if (existsInPage) {
          commit(UPDATE_NOTIFICATION, updated);
        }
        return updated;
      }
      return null;
    } catch {
      return null;
    }
  },

  async [BULK_UPDATE_NOTIFICATIONS]({ commit, state, dispatch }, { notificationIds = [], isRead = true } = {}) {
    if (!Array.isArray(notificationIds) || notificationIds.length === 0) {
      return {
        updated_count: 0,
        failed_ids: [],
        invalid_ids: [],
        not_found_ids: [],
        message: vuetify.framework.lang.t('$vuetify.notifications.bulk_update_no_ids')
      };
    }
    try {
      const body = { notification_ids: notificationIds, is_read: !!isRead };
      const res = await PulseApi.put("notify/bulk", body);
      const data = res.data || {};

      const failedIdsFromApi = Array.isArray(data.failed_ids) ? data.failed_ids : [];
      const notFoundFromApi = Array.isArray(data.not_found_ids) ? data.not_found_ids : [];
      const invalidIdsFromApi = Array.isArray(data.invalid_ids) ? data.invalid_ids : [];

      const successfulIds = notificationIds.filter(id =>
        !failedIdsFromApi.includes(id) && !notFoundFromApi.includes(id) && !invalidIdsFromApi.includes(id)
      );

      if (successfulIds.length) {
        const readAt = isRead ? new Date().toISOString() : null;
        successfulIds.forEach(id => {
          commit(UPDATE_DROPDOWN_NOTIFICATION, { id, is_read: !!isRead, read_at: readAt });
          const idx = state.notifications.findIndex(item => String(item.id) === String(id));
          if (idx !== -1) {
            commit(UPDATE_NOTIFICATION, { id, is_read: !!isRead, read_at: readAt });
          }
        });
      }
      return data;
    } catch (e) {
      const errorMessage = 
        e?.response?.data?.message ||
        e?.message ||
        vuetify.framework.lang.t('$vuetify.notifications.bulk_update_failed');

      return {
        updated_count: 0,
        failed_ids: notificationIds,
        invalid_ids: [],
        not_found_ids: [],
        message: errorMessage
      };
    }
  }
};

const getters = {
  notifications: state => state.notifications,
  dropdownNotifications: state => state.dropdownNotifications,
  unreadCount: state => state.unreadCount,
  suppressBadge: state => state.suppressBadge,
  lastSeenAt: state => state.lastSeenAt,
  getNotificationById: state => id => {
    if (!id) return null;
    return (state.notifications || []).find(notification => String(notification.id) === String(id))
      || (state.dropdownNotifications || []).find(notification => String(notification.id) === String(id))
      || null;
  }
};

export const notification = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
